import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useEffect } from "react";
import ReactMarkdown from "react-markdown";

import {
  BodyText,
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Skeleton,
} from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking/tracking";
import { getDisplay } from "@curai/js-fhir-tools";
import { amazonCarePlanPreviewAtom } from "../atoms/amazonCarePlanPreview";
import { useCarePlanPreview } from "./useCarePlanPreview";

export function AmazonCarePlanPreviewModal({
  isCarePlanSending = false,
  onSendCareplan,
}: { isCarePlanSending?: boolean; onSendCareplan?: () => void }) {
  const [state, setState] = useAtom(amazonCarePlanPreviewAtom);
  const queryClient = useQueryClient();

  const handleOpenChange = (open: boolean) => {
    setState((prev) => ({
      ...prev,
      open,
    }));
  };

  const {
    carePlanId,
    childEncounterId,
    condition,
    motherEncounterId,
    open,
    patientId,
  } = state;

  const name = condition?.code ? getDisplay(condition.code) : "Unknown EOC";

  const {
    data,
    isFetching: isLoadingPreview,
    isError: isErrorPreview,
    error: previewError,
  } = useCarePlanPreview({
    carePlanId,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Always invalidate on open or close for safety
  useEffect(() => {
    if (carePlanId && patientId) {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.PREVIEW({
          patientId,
          carePlanId,
        }),
      });
    }
  }, [open, queryClient, carePlanId, patientId]);

  // Don't render if we don't have needed data
  if (!condition || !carePlanId || !childEncounterId || !motherEncounterId) {
    return null;
  }

  const isLoading = isLoadingPreview;
  const isError = isErrorPreview;
  const isMissingRequiredFields = !isLoading && previewError?.status === 412;
  const isFatalError = !isLoading && isError && !isMissingRequiredFields;

  const closeAndSendCareplan = async () => {
    trackEvent(TRACKERS.SEND_CARE_PLAN_PREVIEW_MODAL);
    // feature/Charting/RoomOptions/RoomOptions.tsx passes in the wrapped close
    // encounter function, which handles toasts and navigation
    handleOpenChange(false);
    await onSendCareplan?.();
  };

  const cancelDialog = () => {
    trackEvent(TRACKERS.CANCEL_SEND_CARE_PLAN);
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className="w-full max-w-md"
          runOnDialogCloseIconClick={cancelDialog}
          onInteractOutside={cancelDialog}
        >
          <DialogHeader>
            <DialogTitle>
              Care Plan Preview: <BodyText>{name}</BodyText>
            </DialogTitle>
            <DialogDescription className="hidden">
              care plan preview modal
            </DialogDescription>
          </DialogHeader>
          {isMissingRequiredFields && (
            <Callout
              className="w-full"
              variant="warning"
              title="Missing Required Details"
              description="For a referral to be sent, a reason, provider type, and urgency are required. Ensure all fields are filled out and try again."
            />
          )}
          {isFatalError && (
            <Callout
              className="w-full"
              variant="danger"
              title="Failed to load care plan preview!"
              description="Please refresh and try again."
            />
          )}

          {isLoading && (
            <div className="grid gap-1">
              {Array.from({ length: 8 }).map((_, i) => (
                //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
                <Skeleton className="h-8" key={i} />
              ))}
            </div>
          )}

          {!isLoading && !isError && (
            <div className="max-h-[70vh] overflow-y-auto">
              <BodyText size="sm" preserveLineBreaks>
                <ReactMarkdown>{data}</ReactMarkdown>
              </BodyText>
            </div>
          )}

          {!isLoading && !isError && (
            <div className="flex flex-row gap-2 justify-self-end">
              <Button
                className="w-fit ml-auto"
                size="sm"
                onClick={closeAndSendCareplan}
                isLoading={isCarePlanSending}
              >
                Send & Close Visit
              </Button>
            </div>
          )}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
